<template>
    <div class="student-portal-forum" :class="{ dark }">
        <div class="student-portal-forum__inner">
            <div class="forum-workspace" :class="{ 'is-loading': loading && !hasReady }">
            <div class="forum-workspace__list">
            <div class="forum-workspace__body">
            <header class="student-portal-forum__header">
                <div>
                    <h1 class="student-portal-forum__title">{{ $t('student.forum.title') }}</h1>
                    <p class="student-portal-forum__subtitle">
                        {{ $t('student.forum.subtitle') }}
                    </p>
                </div>
                <v-btn
                    variant="outlined"
                    prepend-icon="mdi-refresh"
                    class="forum-refresh text-none"
                    :loading="loading"
                    @click="fetchForums"
                >
                    {{ $t('common.update') }}
                </v-btn>
            </header>

            <div v-if="loading && !hasReady" class="forum-loading">
                <v-progress-circular indeterminate color="#1ac7d0" size="48" />
                <p>{{ $t('student.forum.loading') }}</p>
            </div>

            <template v-else>
                <section class="forum-stats" :aria-label="$t('student.forum.title')">
                    <article class="forum-stat forum-stat--forums">
                        <span class="forum-stat__icon" aria-hidden="true">
                            <fa icon="fa-solid fa-comments" />
                        </span>
                        <div>
                            <p class="forum-stat__value">{{ forumStats.forums }}</p>
                            <p class="forum-stat__label">{{ $t('student.forum.title') }}</p>
                            <p class="forum-stat__hint">{{ $t('student.forum.statsForumsHint') }}</p>
                        </div>
                    </article>
                    <article class="forum-stat forum-stat--comments">
                        <span class="forum-stat__icon" aria-hidden="true">
                            <fa icon="fa-solid fa-comment" />
                        </span>
                        <div>
                            <p class="forum-stat__value">{{ forumStats.comments }}</p>
                            <p class="forum-stat__label">{{ $t('student.forum.statsComments') }}</p>
                            <p class="forum-stat__hint">{{ $t('student.forum.statsCommentsHint') }}</p>
                        </div>
                    </article>
                    <article class="forum-stat forum-stat--authors">
                        <span class="forum-stat__icon" aria-hidden="true">
                            <fa icon="fa-solid fa-user-group" />
                        </span>
                        <div>
                            <p class="forum-stat__value">{{ forumStats.authors }}</p>
                            <p class="forum-stat__label">{{ $t('student.forum.statsAuthors') }}</p>
                            <p class="forum-stat__hint">{{ $t('student.forum.statsAuthorsHint') }}</p>
                        </div>
                    </article>
                    <article class="forum-stat forum-stat--recent">
                        <span class="forum-stat__icon" aria-hidden="true">
                            <fa icon="fa-solid fa-clock" />
                        </span>
                        <div>
                            <p class="forum-stat__value">{{ forumStats.recent }}</p>
                            <p class="forum-stat__label">{{ $t('student.filters.last7Days') }}</p>
                            <p class="forum-stat__hint">{{ $t('student.forum.statsRecentHint') }}</p>
                        </div>
                    </article>
                </section>

                <div class="forum-filters">
                    <v-text-field
                        v-model.trim="filters.query"
                        :placeholder="$t('student.forum.searchByTitleOrDesc')"
                        :aria-label="$t('student.forum.searchByTitleOrDesc')"
                        variant="outlined"
                        density="compact"
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                    />
                    <v-select
                        v-model="filters.author"
                        :items="authorFilterOptions"
                        item-title="label"
                        item-value="value"
                        :aria-label="$t('student.forum.author')"
                        :label="$t('student.forum.author')"
                        variant="outlined"
                        density="compact"
                        hide-details
                    />
                    <v-select
                        v-model="filters.period"
                        :items="localizedPeriodFilterOptions"
                        item-title="label"
                        item-value="value"
                        :aria-label="$t('student.filters.period')"
                        :label="$t('student.filters.period')"
                        variant="outlined"
                        density="compact"
                        hide-details
                    />
                    <v-btn
                        variant="outlined"
                        prepend-icon="mdi-filter-off-outline"
                        class="forum-filters__clear text-none"
                        :disabled="!hasActiveFilters"
                        @click="clearFilters"
                    >
                        {{ $t('student.forum.clearFilters') }}
                    </v-btn>
                </div>

                <ul v-if="paginatedForums.length" class="forum-list">
                    <li v-for="forum in paginatedForums" :key="forumKey(forum)">
                        <ForumListItem
                            :forum="forum"
                            :active="isSelected(forum)"
                            @open="goToForum(forum)"
                        />
                    </li>
                </ul>

                <div v-else class="forum-empty">
                    <span class="forum-empty__icon" aria-hidden="true">
                        <fa icon="fa-solid fa-comments" />
                    </span>
                    <h3>{{ $t('student.forum.noForumFound') }}</h3>
                    <p>
                        {{ hasActiveFilters ? $t('student.forum.emptyFiltered') : $t('student.forum.empty') }}
                    </p>
                    <div v-if="hasActiveFilters" class="forum-empty__action">
                        <v-btn
                            variant="outlined"
                            class="forum-filters__clear text-none"
                            @click="clearFilters"
                        >
                            {{ $t('student.forum.clearFilters') }}
                        </v-btn>
                    </div>
                </div>
            </template>
            </div>

                <footer v-if="hasReady && filteredForums.length" class="forum-pager">
                    <p class="forum-pager__range">{{ showingLabel }}</p>
                    <div class="forum-pager__controls">
                        <label class="forum-pager__size">
                            {{ $t('student.forum.perPage') }}
                            <select
                                :value="itemsPerPage"
                                :aria-label="$t('student.forum.perPage')"
                                @change="changeItemsPerPage($event.target.value)"
                            >
                                <option v-for="size in pageSizeOptions" :key="size" :value="size">
                                    {{ size }}
                                </option>
                            </select>
                        </label>
                        <button
                            type="button"
                            :aria-label="$t('student.forum.previousPage')"
                            :disabled="currentPage <= 1"
                            @click="page = currentPage - 1"
                        >
                            <v-icon size="18">mdi-chevron-left</v-icon>
                        </button>
                        <span :aria-label="$t('student.forum.pageNumber', { n: currentPage })">
                            {{ currentPage }}
                        </span>
                        <button
                            type="button"
                            :aria-label="$t('student.forum.nextPage')"
                            :disabled="currentPage >= pageCount"
                            @click="page = currentPage + 1"
                        >
                            <v-icon size="18">mdi-chevron-right</v-icon>
                        </button>
                    </div>
                </footer>
            </div>
            <ForumDetailPanel
                v-if="!(loading && !hasReady)"
                :forum="selectedForum"
                @back="clearSelection"
                @count="syncCommentCount"
            />
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/utils/portal/forum.js';
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';
import ForumListItem from '@/components/Student/PortalForum/ForumListItem.vue';
import ForumDetailPanel from '@/components/Student/PortalForum/ForumDetailPanel.vue';

export default {
    components: { ForumListItem, ForumDetailPanel },
    data() {
        return {
            loading: false,
            hasReady: false,
            forums: [],
            selectedForum: null,
            selectionCleared: false,
            itemsPerPage: 10,
            page: 1,
            pageSizeOptions: [10, 25, 50, 100],
            filters: {
                query: '',
                author: 'all',
                period: 'all',
            },
        };
    },
    computed: {
        ...mapState(['token', 'dark']),
        localizedPeriodFilterOptions() {
            return [
                { label: this.$t('student.filters.allPeriod'), value: 'all' },
                { label: this.$t('student.filters.last7Days'), value: 7 },
                { label: this.$t('student.filters.last30Days'), value: 30 },
                { label: this.$t('student.filters.last90Days'), value: 90 },
            ];
        },
        shouldHidePagination() {
            return this.filteredForums.length <= this.itemsPerPage;
        },
        pageCount() {
            return Math.max(1, Math.ceil(this.filteredForums.length / this.itemsPerPage) || 1);
        },
        currentPage() {
            return Math.min(this.page, this.pageCount);
        },
        paginatedForums() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredForums.slice(start, start + this.itemsPerPage);
        },
        visiblePages() {
            const total = this.pageCount;
            const current = this.currentPage;
            const size = 5;
            let start = Math.max(1, current - 2);
            let end = Math.min(total, start + size - 1);
            start = Math.max(1, end - size + 1);
            const pages = [];
            for (let number = start; number <= end; number += 1) pages.push(number);
            return pages;
        },
        showingLabel() {
            const total = this.filteredForums.length;
            if (!total) return '';
            const from = (this.currentPage - 1) * this.itemsPerPage + 1;
            const to = Math.min(total, this.currentPage * this.itemsPerPage);
            const key = total === 1 ? 'student.forum.showingOne' : 'student.forum.showingMany';
            return this.$t(key, { from, to, total });
        },
        forumStats() {
            const comments = this.forums.reduce(
                (sum, item) => sum + Number(item.commentsQty || 0),
                0
            );
            const authors = new Set(
                this.forums
                    .map((item) => String(item.userName || '').trim())
                    .filter((name) => name.length > 0)
            ).size;
            const now = Date.now();
            const recent = this.forums.filter((item) => {
                if (!item.dateInsert) return false;
                const date = new Date(item.dateInsert);
                if (Number.isNaN(date.getTime())) return false;
                return (now - date.getTime()) / (1000 * 60 * 60 * 24) <= 7;
            }).length;
            return {
                forums: this.forums.length,
                comments,
                authors,
                recent,
            };
        },
        hasActiveFilters() {
            return (
                this.filters.query !== '' ||
                this.filters.author !== 'all' ||
                this.filters.period !== 'all'
            );
        },
        authorFilterOptions() {
            const names = Array.from(
                new Set(
                    this.forums
                        .map((item) => String(item.userName || '').trim())
                        .filter((name) => name.length > 0)
                )
            ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

            return [
                { label: this.$t('student.forum.allAuthors'), value: 'all' },
                ...names.map((name) => ({ label: name, value: name })),
            ];
        },
        filteredForums() {
            const q = String(this.filters.query || '').toLowerCase();
            const author = this.filters.author;
            const period = this.filters.period;
            const now = new Date();

            return this.forums.filter((item) => {
                const byQuery =
                    !q ||
                    String(item.title || '').toLowerCase().includes(q) ||
                    String(item.description || '').toLowerCase().includes(q);

                const byAuthor =
                    author === 'all' || String(item.userName || '').trim() === author;

                let byPeriod = true;
                if (period !== 'all' && item.dateInsert) {
                    const dt = new Date(item.dateInsert);
                    if (Number.isNaN(dt.getTime())) {
                        byPeriod = false;
                    } else {
                        const diffMs = now.getTime() - dt.getTime();
                        const diffDays = diffMs / (1000 * 60 * 60 * 24);
                        byPeriod = diffDays <= Number(period);
                    }
                }

                return byQuery && byAuthor && byPeriod;
            });
        },
    },
    watch: {
        'filters.query'() {
            this.page = 1;
        },
        'filters.author'() {
            this.page = 1;
        },
        'filters.period'() {
            this.page = 1;
        },
        filteredForums: {
            handler() {
                this.ensureSelection();
            },
        },
    },
    methods: {
        async fetchForums() {
            this.loading = true;
            try {
                const response = await api.getDataForum(this.token);
                this.forums = Array.isArray(response) ? response : [];
            } catch (e) {
                toast.error(this.$t('student.forum.toastLoadError'));
                console.error('Erro ao carregar fóruns', e);
            } finally {
                this.loading = false;
                this.hasReady = true;
            }
        },
        goToForum(forum) {
            const source = forum?.raw || forum;
            const id = source?.id ?? source?.idForum ?? source?.slug;
            if (id == null || id === '') return;
            this.selectionCleared = false;
            this.selectedForum = source;
        },
        clearSelection() {
            this.selectionCleared = true;
            this.selectedForum = null;
        },
        isSelected(forum) {
            if (!this.selectedForum) return false;
            return this.forumKey(forum) === this.forumKey(this.selectedForum);
        },
        ensureSelection() {
            const visible = this.filteredForums;
            if (this.selectedForum) {
                const current = visible.find((item) => this.forumKey(item) === this.forumKey(this.selectedForum));
                if (current) {
                    this.selectedForum = current;
                    return;
                }
            }
            if (this.selectionCleared || !visible.length) {
                this.selectedForum = null;
                return;
            }
            this.selectedForum = visible[0];
        },
        syncCommentCount(count) {
            if (!this.selectedForum) return;
            const key = this.forumKey(this.selectedForum);
            const target = this.forums.find((item) => this.forumKey(item) === key);
            if (!target) return;
            target.commentsQty = count;
            if (this.selectedForum !== target) this.selectedForum.commentsQty = count;
        },
        clearFilters() {
            this.filters.query = '';
            this.filters.author = 'all';
            this.filters.period = 'all';
        },
        changeItemsPerPage(value) {
            const size = Number(value);
            this.itemsPerPage = this.pageSizeOptions.includes(size) ? size : 10;
            this.page = 1;
        },
        forumKey(forum) {
            const source = forum?.raw || forum || {};
            return source.id ?? source.idForum ?? source.slug ?? source.title;
        },
    },
    mounted() {
        this.fetchForums();
    },
};
</script>

<style src="./css/forum-portal.css"></style>
